import React from 'react';
import { Card, Timeline, Tag, Empty, Spin, Alert, Tooltip } from 'antd';
import {
  ClockCircleOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  PauseCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useScheduleOverview } from '../../../hooks/useManufacturingDashboard';

interface ScheduleGanttChartProps {
  startDate: string;
  endDate: string;
}

const ScheduleGanttChart: React.FC<ScheduleGanttChartProps> = ({ startDate, endDate }) => {
  const { data: schedules, isLoading, error } = useScheduleOverview(startDate, endDate);

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      'Pending': '#d9d9d9',
      'In Progress': '#145A54',
      'Completed': '#52c41a',
      'On Hold': '#faad14',
      'Skipped': '#8c8c8c',
    };
    return colors[status] || '#d9d9d9';
  };

  const getStatusIcon = (status: string) => {
    const icons: Record<string, React.ReactNode> = {
      'Pending': <ClockCircleOutlined />,
      'In Progress': <SyncOutlined spin />,
      'Completed': <CheckCircleOutlined />,
      'On Hold': <PauseCircleOutlined />,
    };
    return icons[status] || <ClockCircleOutlined />;
  };

  if (error) {
    return (
      <Card title="Production Schedule">
        <Alert
          message="Error Loading Schedule"
          description={error.message}
          type="error"
          showIcon
        />
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card title="Production Schedule">
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (!schedules || schedules.length === 0) {
    return (
      <Card
        title="Production Schedule"
        extra={
          <span style={{ fontSize: 12, color: '#8c8c8c' }}>
            {dayjs(startDate).format('MMM DD')} - {dayjs(endDate).format('MMM DD, YYYY')}
          </span>
        }
      >
        <Empty description="No scheduled work orders in this period" />
      </Card>
    );
  }

  return (
    <Card
      title="Production Schedule (Timeline View)"
      extra={
        <span style={{ fontSize: 12, color: '#8c8c8c' }}>
          {dayjs(startDate).format('MMM DD')} - {dayjs(endDate).format('MMM DD, YYYY')}
        </span>
      }
    >
      <div style={{ maxHeight: 600, overflowY: 'auto' }}>
        {schedules.map((schedule) => (
          <div key={schedule.workOrderId} style={{ marginBottom: 32 }}>
            <div style={{ marginBottom: 12, paddingLeft: 16, borderLeft: '3px solid #145A54' }}>
              <div style={{ fontWeight: 500, fontSize: 14 }}>
                {schedule.workOrderNumber} - {schedule.itemName}
              </div>
              <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                {schedule.stages.length} stages
              </div>
            </div>

            <Timeline
              items={schedule.stages.map((stage, index) => {
                const startTime = dayjs(stage.plannedStart);
                const endTime = dayjs(stage.plannedEnd);
                const duration = endTime.diff(startTime, 'hour', true);
                const isDelayed = stage.actualStart && stage.actualEnd &&
                  dayjs(stage.actualEnd).isAfter(dayjs(stage.plannedEnd));

                return {
                  color: getStatusColor(stage.status),
                  dot: getStatusIcon(stage.status),
                  children: (
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <strong>{stage.stageName}</strong>
                        <Tag color={getStatusColor(stage.status)} style={{ margin: 0 }}>
                          {stage.status}
                        </Tag>
                        {isDelayed && (
                          <Tag color="red">Delayed</Tag>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 8 }}>
                        <div>Workstation: {stage.workstation}</div>
                        <div>
                          Planned: {startTime.format('MMM DD, HH:mm')} - {endTime.format('HH:mm')}
                          {' '}({duration.toFixed(1)}h)
                        </div>
                        {stage.actualStart && (
                          <div>
                            Actual: {dayjs(stage.actualStart).format('MMM DD, HH:mm')}
                            {stage.actualEnd && ` - ${dayjs(stage.actualEnd).format('HH:mm')}`}
                          </div>
                        )}
                      </div>
                      {stage.status === 'In Progress' && (
                        <div style={{ maxWidth: 200 }}>
                          <Tooltip title={`${stage.progress.toFixed(1)}% completed`}>
                            <div style={{
                              height: 8,
                              background: '#f0f0f0',
                              borderRadius: 4,
                              overflow: 'hidden',
                            }}>
                              <div style={{
                                height: '100%',
                                width: `${stage.progress}%`,
                                background: '#145A54',
                                transition: 'width 0.3s',
                              }} />
                            </div>
                          </Tooltip>
                        </div>
                      )}
                    </div>
                  ),
                };
              })}
            />
          </div>
        ))}
      </div>

      {/* Legend */}
      <div style={{
        marginTop: 24,
        padding: 16,
        background: '#fafafa',
        borderRadius: 4,
        display: 'flex',
        gap: 16,
        flexWrap: 'wrap',
      }}>
        <Tag color="#d9d9d9">Pending</Tag>
        <Tag color="#145A54">In Progress</Tag>
        <Tag color="#52c41a">Completed</Tag>
        <Tag color="#faad14">On Hold</Tag>
        <Tag color="#8c8c8c">Skipped</Tag>
      </div>
    </Card>
  );
};

export default ScheduleGanttChart;
